import * as React from "react";

import { DataTable, Spinner } from "src/components/common";
import { Button } from "src/components/buttons";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";

import PickListContext from "src/context/pickListContext";

import CustomFieldAPI from "./CustomFieldAPI";
import DatasetAPI from "../Datasets/DatasetAPI";
import { Tooltip } from "antd";
import {
	ActionIcon,
	ClearFilterButton,
} from "src/components/forms/ActionColumnIconsAndButton";
import { isEqual, cloneDeep } from "lodash";
import { AdminPermissions } from "src/common/Permissions";
import {
	setStatusCellValue,
	filterByStatus,
	StatusFilterMethod,
} from "src/components/forms/StatusFilter";
import { toast } from "react-toastify";

interface IProps {
	match?: any;
	history?: any;
}

interface IState {
	customFields: any[];
	datasets: DatasetDTO[];
	loading: boolean;
	editMode: boolean;
	originalOrder: any[];
	isLoading: boolean;
	filtered: any[];
}

class OrganizationCustomFields extends React.Component<IProps, IState> {
	static contextType = PickListContext;
	context: React.ContextType<typeof PickListContext>;

	constructor(props: IProps) {
		super(props);
		this.state = {
			customFields: [],
			datasets: [],
			loading: false,
			editMode: false,
			originalOrder: [],
			isLoading: true,
			filtered: [],
		};
	}

	public componentDidMount() {
		const orgId = Number(this.props.match.params.organizationId);
		if (orgId !== 0) {
			const p_customFields = CustomFieldAPI.getCustomFields({
				organizationId: orgId,
			});
			const p_organizationDatasets = DatasetAPI.getOrganizationDatasets(orgId);

			Promise.all([p_customFields, p_organizationDatasets]).then((values) => {
				const [customFields, datasets] = values;

				this.setState({
					customFields: customFields,
					originalOrder: cloneDeep(customFields),
					datasets: datasets,
					isLoading: false,
				});
			});
			this.setState({ filtered: [{ id: "isActive", value: "Active" }] });
		}
	}

	public render() {
		const { customFields, editMode, loading } = this.state;
		const columns = this.getColumns();

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<div className="container-fluid">
				<AuthorizationWrapper
					type="hide"
					permissions={[AdminPermissions.EditOrganizationCustomField]}
				>
					<div className="d-flex mb-4">
						<div className="ml-auto">
							<Button
								className="btn-primary"
								to={`/admin/organizations/${this.props.match.params.organizationId}/custom-fields/0`}
							>
								New Field
							</Button>
						</div>
					</div>
				</AuthorizationWrapper>
				<DataTable
					data={customFields.sort((a, b) => a.viewOrder - b.viewOrder)}
					columns={columns}
					noDataText="There are no custom fields available"
					filterable={true}
					filtered={this.state.filtered}
					onFilteredChange={(filtered: any) => this.setState({ filtered })}
					showPagination={false}
					loading={loading}
					onRowClick={editMode ? undefined : this.onRowClick}
				/>
			</div>
		);
	}

	private editOrderAndSave = async () => {
		const { editMode, customFields, originalOrder } = this.state;
		const organizationId = Number(this.props.match.params.organizationId);
		if (!editMode) {
			this.setState((prev) => ({ ...prev, editMode: !prev.editMode }));
		} else {
			// Make a post request to save the order
			// compare the new order to oiginalOrder array, if nothing has changed dont make the request.
			const customFieldIds = customFields.map((cf) => cf.id);
			const isNewOrderSame: boolean = isEqual(
				customFieldIds,
				originalOrder.map((o) => o.id)
			);
			if (isNewOrderSame) {
				this.setState((prev) => ({ ...prev, editMode: !prev.editMode }));
				return;
			} else {
				// Now make a post request
				try {
					const orderList = customFields.map((cf) => cf.viewOrder);
					const res = await CustomFieldAPI.updateCustomFieldOrder({
						organizationId,
						customFieldIds,
						orderList,
					});
					if (res) {
						toast.success("Order update successful");
						this.setState((prev) => ({
							...prev,
							originalOrder: prev.customFields,
						}));
					}
					this.setState((prev) => ({ ...prev, editMode: !prev.editMode }));
				} catch (error) {
					toast.error("Unable to update order");
					this.restoreOldOrder();
				}
			}
		}
	};

	private restoreOldOrder = () => {
		const { originalOrder } = this.state;
		const restoredCustomFields = cloneDeep(originalOrder);
		this.setState((prev: IState) => ({
			editMode: !prev.editMode,
			customFields: restoredCustomFields,
		}));
	};

	private setRowValue = (picklistId: number) => {
		const item = this.context.pickListItems.find(
			(pItem: PickListItemResponseDTO) => pItem.id! === picklistId
		);
		if (item) {
			return item.description!;
		} else {
			return "";
		}
	};

	private setMultipleLabelComponent = (labels: JSX.Element[]) => {
		const displayText = labels.map((l) => l.key).join(", ");
		return displayText;
	};

	private getSingleDatasetName = (datasetId: number) => {
		const datasets = [...this.state.datasets];
		const foundDataset = datasets.find(
			(set) => set.id === datasetId
		) as DatasetDTO;
		if (foundDataset !== undefined) {
			return foundDataset.name!;
		}

		return "";
	};

	private getMultipleDatasetNames = (datasetIds: number[]) => {
		const datasets = [...this.state.datasets];

		const convertedDatasetNames = datasets
			.filter((dSet) => datasetIds.includes(dSet.id!))
			.map((foundDset) => <p key={foundDset.name!}>{foundDset.name!}</p>);

		const datasetLabelComponent = this.setMultipleLabelComponent(
			convertedDatasetNames
		);
		return datasetLabelComponent;
	};

	private setRowDatasetValue = (datasetIds: number[]) => {
		if (datasetIds.length === 1) {
			const singleDatasetName = this.getSingleDatasetName(datasetIds[0]);
			return singleDatasetName;
		}

		if (datasetIds.length > 1) {
			const multipleDatasetNames = this.getMultipleDatasetNames(datasetIds);
			return multipleDatasetNames;
		}

		const noDatasetName = "";
		return noDatasetName;
	};

	private getActionLink(orgId: any, rowId: any): string {
		return `/admin/organizations/${orgId}/custom-fields/${rowId}`;
	}

	private onRowClick = ({ original }: { original?: any }) => {
		this.props.history.push(
			this.getActionLink(this.props.match.params.organizationId!, original.id)
		);
	};

	private getColumns = () => {
		const columns = [
			{
				Header: <Tooltip title="Name">Name</Tooltip>,
				accessor: "name",
			},
			{
				Header: <Tooltip title="Active">Active</Tooltip>,
				accessor: "isActive",
				Cell: setStatusCellValue,
				filterMethod: filterByStatus("isActive"),
				Filter: StatusFilterMethod,
			},
			{
				Header: <Tooltip title="Kind">Kind</Tooltip>,
				id: "controlTypeId",
				accessor: (row: any) => this.setRowValue(row.controlTypeId),
			},
			{
				Header: <Tooltip title="Custom Field Type">Custom Field Type</Tooltip>,
				id: "customFieldType",
				accessor: (row: any) => this.setRowValue(row.customFieldType),
			},
			{
				Header: <Tooltip title="Export">Export</Tooltip>,
				id: "includeInExport",
				accessor: (row: any) =>
					row.includeInExport === true ? "True" : "False",
			},
			{
				Header: <Tooltip title="Required">Required</Tooltip>,
				id: "required",
				accessor: (row: any) => (row.required === true ? "True" : "False"),
			},
			{
				Header: <Tooltip title="PII/PHI">PII/PHI</Tooltip>,
				id: "phi",
				accessor: (row: any) => (row.phi === true ? "True" : "False"),
			},
			{
				Header: <Tooltip title="Dataset">Dataset</Tooltip>,
				id: "datasetIds",
				accessor: (row: any) => this.setRowDatasetValue(row.datasetIds),
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: (
					<ActionIcon
						title="Details"
						type="open"
						disabled={this.state.editMode}
					/>
				),
				Filter: (
					<ClearFilterButton
						onClick={() =>
							this.setState({ filtered: [{ id: "isActive", value: "Active" }] })
						}
					/>
				),
			},
		];
		return columns;
	};
}

export default OrganizationCustomFields;
